import { cva } from 'class-variance-authority'

/*
 * `.section-container` and `.section-wrapper`, ported verbatim.
 *
 * These two are the spine of the whole site: 277 and 252 uses respectively.
 * Every modifier below maps 1:1 to a Webflow class, so a section in the mirror
 * translates by reading its class list left to right.
 *
 *   .section-container       gap 32, bg white, column, px 24, relative
 *   .section-wrapper         z10, gap 64, row, max-w 1240, min-h 100, mx auto
 *
 * Kept in its own module so Section.tsx exports only components.
 */
/*
 * `.section-container`, on four axes instead of nine.
 *
 * The nine came from transliterating Webflow's modifier classes one-to-one:
 * a `padding` enum plus `hero`, `bottomCta`, `extraPaddingBottom`,
 * `reducePaddingBottom`, `paddingTop0` and `paddingBottom0` as separate
 * booleans. That was the right move for a migration — it is what made the
 * ported markup verifiable — but it is not an API. Four of the booleans were
 * one axis, and `bottomCta` silently overrode `surface`, so two props could
 * disagree with no type error.
 *
 * `hero` and `bottomCta` were always `surface` + `padding` combinations, so
 * they are exactly that at the call site now:
 *
 *   .page-hero        -> paddingTop="3xl" paddingBottom="xl"
 *   .bottom-cta       -> surface="dark"   padding="2xl"
 *
 * Padding uses the `--spacing-section-*` scale named in Phase 2, so a value
 * here is the same value the rest of the system spells.
 */
export const sectionVariants = cva('relative flex flex-col gap-8 bg-white px-6', {
  variants: {
    surface: {
      default: '',
      /** .dark — note Webflow also sets `color` to the same value. */
      dark: 'items-start justify-start bg-neutral-900',
      /**
       * `.bottom-cta`'s ground. Webflow reached this through a compound —
       * `.section-container.section-padding-large.dark` — so it looked like a
       * padding side effect. It is a surface: --dark-blue-900, not
       * --neutral-900, and every BottomCTA on the site renders it.
       */
      darkBlue: 'items-start justify-start bg-dark-blue-900',
      gray: 'bg-grayscale-10',
      /** The warm white of /studio-for-jewelers. */
      ivory: 'bg-ivory',
      grayDeep: 'bg-neutral-100',
      purpleLight: 'bg-purple-light',
      cyan: 'bg-cyan-100',
    },
    padding: {
      none: '',
      xs: 'py-section-xs',
      sm: 'py-section-sm',
      md: 'py-section-md',
      lg: 'py-section-lg',
      xl: 'py-section-xl',
      '2xl': 'py-section-2xl',
      '3xl': 'py-section-3xl',
    },
    paddingTop: {
      none: 'pt-0',
      xs: 'pt-section-xs',
      sm: 'pt-section-sm',
      md: 'pt-section-md',
      lg: 'pt-section-lg',
      xl: 'pt-section-xl',
      '2xl': 'pt-section-2xl',
      '3xl': 'pt-section-3xl',
    },
    paddingBottom: {
      none: 'pb-0',
      xs: 'pb-section-xs',
      sm: 'pb-section-sm',
      md: 'pb-section-md',
      lg: 'pb-section-lg',
      xl: 'pb-section-xl',
      '2xl': 'pb-section-2xl',
      '3xl': 'pb-section-3xl',
    },
    /**
     * Webflow's `.hidden` sets `overflow: hidden`, NOT `display: none`.
     * Renamed to `clipped` here — `hidden` collides with the HTML attribute
     * and reads as the opposite of what it does.
     */
    clipped: { true: 'overflow-hidden' },
  },
  defaultVariants: { surface: 'default', padding: 'none' },
})

export const sectionWrapperVariants = cva(
  /*
   * No `max-w-*` here. It used to carry `max-w-main`, which every `width`
   * variant then had to beat — and tailwind-merge does not know these custom
   * container keys are one conflict group, so both classes survived and the
   * cascade picked by emit order. `blogPost` (740px) and `content` (760px)
   * silently lost to the 1240px base. The width now comes from exactly one
   * place: the `width` variant below.
   */
  'relative z-10 mx-auto flex w-full min-h-[100px] flex-row items-start justify-start gap-16',
  {
    variants: {
      layout: {
        default: '',
        /** .stack */
        stack: 'flex-col items-start justify-start',
        /** .center */
        center: 'flex-col items-center justify-between gap-6',
        /** .row */
        row: 'gap-16',
        /** .middle */
        middle: 'items-center justify-start',
      },
      /** .center as a modifier ON .stack — distinct from layout="center". */
      centered: { true: 'items-center justify-start' },
      gap: {
        default: '',
        /*
         * `.gap` is `grid-gap: 32px`. This used to be `py-section-xs` — 30px
         * of *padding* — which came from the compound
         * `.section-wrapper.stack.center.gap`, the only place live pairs the
         * class with padding. Collapsing that compound into the base variant
         * gave every `gap="gap"` call site vertical padding and no gap at all.
         */
        gap: 'gap-8',
        /** .gap-large */
        large: 'gap-16',
        /** .double-gap */
        double: 'gap-20',
      },
      width: {
        default: 'max-w-main',
        /** .small / .maxwidthsmall */
        small: 'max-w-small',
        /** .maxwidthcontent */
        content: 'max-w-content',
        /** .blog-post */
        blogPost: 'max-w-blog-post',
        /** .maxwidthmedium */
        medium: 'max-w-medium',
        /** .maxwidthlarge */
        large: 'max-w-main',
      },
      padding: {
        none: '',
        /** .padding */
        all: 'p-16',
        /** .paddingysmall */
        ySmall: 'py-section-sm',
        /** .extrapaddingbottom */
        extraBottom: 'pb-16',
      },
      /** .outline-card */
      outlineCard: { true: 'rounded-2xl border border-solid border-neutral-200' },
    },
    defaultVariants: { layout: 'default', gap: 'default', width: 'default', padding: 'none' },
  },
)
